import { useCallback, useEffect, useState, useMemo } from 'react';
import FJLocalStore from '../../../store/FJLocalStore.js';
import DownloadIcon from '../../../assets/svg/download_black.svg?react';
import styles from './FJMultiSourceDownloadButton.module.less';
import FJDropdownNew from '../../components/FJDropDown/FJDropdownNew.jsx';
import FJUtil from '../../../util/FJUtil.js';
import PropTypes from 'prop-types';
import FJCheckBox from '../../components/FJCheckBox/FJCheckBox.jsx';
import _ from 'lodash';
import FJToolTip from '../../components/FJToolTip/FJToolTip.jsx';
import FJButton, { FJButtonType } from '../../components/FJButton/FJButton.jsx';
import FJDownloadGradientIcon from '../../../assets/svg/download-gradient.svg?react';
import classNames from 'classnames';

const Item = props => {
    const { id, url, name, isSelected = false, onClick } = props;

    const _onClick = selected => {
        onClick && onClick(id, selected);
    };

    return (
        <div className={styles.item}>
            <div className={styles.item_left}>
                <div className={styles.img_box}>
                    <img className={styles.item_img} src={url} alt={''} />
                </div>
                <div className={styles.item_name}>{name}</div>
            </div>
            <FJCheckBox selected={isSelected} dataChange={_onClick} />
        </div>
    );
};

const FJMultiSourceDownloadButton = props => {
    const {
        list = [],
        selectedDownloadList = [],
        isDownloading = false,
        onDownload,
        selectAll,
        deselectAll,
        selectItem,
        disableDownloadMultipleSource = false, // 是否禁用同时下载多个资源
    } = props;

    const isMultipleSource = useMemo(() => {
        return list.length > 1 && !disableDownloadMultipleSource;
    }, [list.length, disableDownloadMultipleSource]);

    // 是否显示按钮文字
    const [isNormalButton, setIsNormalButton] = useState(true);

    const _update = useCallback(() => {
        setIsNormalButton(document.body.offsetWidth >= 1400);
    }, []);

    useEffect(() => {
        _update();
        const resizeCallback = _.debounce(_update, 200, { trailing: true });
        window.addEventListener('resize', resizeCallback);
        return () => {
            window.removeEventListener('resize', resizeCallback);
        };
    }, [_update]);

    const _selectAll = useCallback(
        isSelected => {
            if (isSelected) {
                selectAll && selectAll();
            } else {
                deselectAll && deselectAll();
            }
        },
        [selectAll, deselectAll],
    );

    const _onSelectItem = useCallback(
        (id, selected) => {
            selectItem && selectItem(id, selected);
        },
        [selectItem],
    );

    const _isSelected = useCallback(
        id => {
            return selectedDownloadList && selectedDownloadList.findIndex(item => item.id === id) !== -1;
        },
        [selectedDownloadList],
    );

    const _isSelectedAll = useMemo(() => {
        return list.every(item => _isSelected(item.id));
    }, [list, _isSelected]);

    const _renderDownIcon = useCallback(() => {
        return (
            <>
                <DownloadIcon className={styles.download_icon} />
                <FJDownloadGradientIcon className={styles.download_gradient_icon} />
            </>
        );
    }, []);

    const _renderOverlay = useCallback(
        config => {
            return (
                <div className={styles.download_menu_box} ref={config.ref}>
                    <div className={styles.select_images_title}>{FJLocalStore._('select-images')}</div>
                    <div className={styles.menu_box}>
                        <div className={styles.select_all_box}>
                            <div className={styles.select_all_title}>
                                {FJLocalStore._('all-images') + ` (${list.length})`}
                            </div>
                            <FJCheckBox selected={_isSelectedAll} dataChange={_selectAll} />
                        </div>
                        <div className={styles.list_box}>
                            {list.map((data, i) => {
                                const { id, url, name = '' } = data;
                                const showName = name ? name : FJUtil.getFileName(url, true);

                                return (
                                    <Item
                                        key={id + i}
                                        id={id}
                                        url={url}
                                        name={showName}
                                        isSelected={_isSelected(id)}
                                        onClick={_onSelectItem}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <FJButton
                        className={classNames(styles.download_button_box, {
                            [styles.only_icon_button]: !isNormalButton,
                        })}
                        type={FJButtonType.LIGHT_GRADIENT}
                        textClassName={styles.button_text}
                        text={FJLocalStore._(isNormalButton ? 'download' : '')}
                        isLoading={isDownloading}
                        icon={_renderDownIcon()}
                        onClick={onDownload}
                        disabled={selectedDownloadList.length === 0}
                    />
                </div>
            );
        },
        [
            list,
            _isSelectedAll,
            _selectAll,
            _isSelected,
            _onSelectItem,
            isNormalButton,
            isDownloading,
            onDownload,
            _renderDownIcon,
            selectedDownloadList,
        ],
    );

    const _renderButton = useCallback(
        (button, title) => {
            if (isNormalButton) return button;
            else
                return (
                    <FJToolTip title={title}>
                        <div>{button}</div>
                    </FJToolTip>
                );
        },
        [isNormalButton],
    );

    const _renderDownloadButton = useCallback(
        config => {
            const isDefaultButton = FJUtil.isEditPage();
            return config ? (
                <div onClick={config.onClick} ref={config.ref}>
                    {_renderButton(
                        <FJButton
                            className={classNames(styles.download_button_box, {
                                [styles.only_icon_button]: !isNormalButton,
                                [styles.default_button]: isDefaultButton,
                            })}
                            type={isDefaultButton ? FJButtonType.DEFAULT : FJButtonType.GRADIENT}
                            textClassName={styles.button_text}
                            text={isNormalButton ? FJLocalStore._('download') : ''}
                            icon={
                                <DownloadIcon className={isDefaultButton ? '' : styles.trigger_download_icon_white} />
                            }
                        />,
                        FJLocalStore._('download'),
                    )}
                </div>
            ) : (
                _renderButton(
                    <FJButton
                        className={classNames(styles.download_button_box, {
                            [styles.only_icon_button]: !isNormalButton,
                            [styles.default_button]: isDefaultButton,
                        })}
                        type={isDefaultButton ? FJButtonType.DEFAULT : FJButtonType.GRADIENT}
                        textClassName={styles.button_text}
                        text={isNormalButton ? FJLocalStore._('download') : ''}
                        icon={<DownloadIcon className={isDefaultButton ? '' : styles.trigger_download_icon_white} />}
                        onClick={onDownload}
                        isLoading={isDownloading}
                    />,
                    FJLocalStore._('download'),
                )
            );
        },
        [isNormalButton, _renderDownIcon, onDownload, isDownloading, _renderButton],
    );

    return (
        <div className={styles.box}>
            {isMultipleSource ? (
                <FJDropdownNew
                    offsetLeft={isNormalButton ? 0 : -77}
                    isTopLayer={true}
                    offsetTop={5}
                    renderTrigger={_renderDownloadButton}
                    renderOverlay={_renderOverlay}
                    placement='bottomRight'
                />
            ) : (
                _renderDownloadButton()
            )}
        </div>
    );
};

FJMultiSourceDownloadButton.propTypes = {
    list: PropTypes.array,
    selectedDownloadList: PropTypes.array,
    isDownloading: PropTypes.bool,
    onDownload: PropTypes.func,
    selectAll: PropTypes.func,
    deselectAll: PropTypes.func,
    selectItem: PropTypes.func,
    disableDownloadMultipleSource: PropTypes.bool,
};

export default FJMultiSourceDownloadButton;
